<script lang="ts" setup>
import virtualCardImg from './assets/virtual-card.png'
import physicalCardImg from './assets/physical-card.png'
import paypalImg from './assets/paypal.png'
import bankAccountImg from './assets/bank-account.png'

// Illustrative-only row of the ways a recipient can receive their funds, shown
// in the demo/preview showcase (beat 9). Purely presentational — the real
// redemption picker lives on the recipient landing page.
interface Option {
  img: string
  title: string
  background: string
  // Centered graphics get a fixed height; the physical-card illustration
  // instead fills the whole card (`fill`), with the label overlaid.
  graphicHeight?: number
  fill?: boolean
}

const options: Option[] = [
  { img: virtualCardImg, title: 'Virtual Card', background: '#f2f1f9', graphicHeight: 38 },
  { img: physicalCardImg, title: 'Physical Card', background: '#fbf1df', fill: true },
  { img: paypalImg, title: 'PayPal', background: '#efecfa', graphicHeight: 52 },
  { img: bankAccountImg, title: 'Bank Account', background: '#eef1f2', graphicHeight: 42 },
]
</script>

<template>
  <div class="redeem-options">
    <div
      v-for="option in options"
      :key="option.title"
      class="redeem-option"
      :class="{ 'is-fill': option.fill }"
      :style="{ background: option.background }"
    >
      <img
        v-if="option.fill"
        class="redeem-option-fill"
        :src="option.img"
        :alt="option.title"
      >
      <div v-else class="redeem-option-graphic">
        <img :src="option.img" :alt="option.title" :style="{ height: `${option.graphicHeight}px` }">
      </div>
      <div class="redeem-option-label">{{ option.title }}</div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.redeem-options {
  display: flex;
  gap: 10px;
  justify-content: center;
  max-width: calc(100vw - 24px);
}

.redeem-option {
  align-items: center;
  border-radius: 18px;
  box-shadow: 0 6px 16px rgb(17 30 54 / 8%);
  display: flex;

  // 90×130 cards (flex-basis 90, capped so all four fit on narrow screens).
  flex: 1 1 90px;
  flex-direction: column;
  justify-content: space-between;
  max-width: 96px;
  min-height: 122px;
  overflow: hidden;
  padding: 14px 6px 12px;
  position: relative;
}

// The physical-card illustration fills the whole card, so its label anchors to
// the bottom rather than following a centered graphic.
.redeem-option.is-fill {
  justify-content: flex-end;
}

// Centered graphic (Virtual Card / PayPal / Bank Account) in the upper area.
.redeem-option-graphic {
  align-items: center;
  display: flex;
  flex-grow: 1;
  justify-content: center;

  img {
    display: block;
    max-width: 100%;
    object-fit: contain;
  }
}

// Full-bleed graphic (Physical Card): covers the whole card.
.redeem-option-fill {
  height: 100%;
  left: 0;
  object-fit: cover;
  position: absolute;
  top: 0;
  width: 100%;
}

.redeem-option-label {
  color: #111e36;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.15;

  // Keep the label above the full-bleed graphic.
  position: relative;
  text-align: center;
  z-index: 1;
}
</style>
